import { useState, useCallback, useEffect } from 'react';
import { Upload, X } from 'lucide-react';
import { useWorkshopProfile } from '@/hooks/useSettings';
import { SpecialtyPicker } from '@mustauto/ui/web';
import { UF_VALUES } from '@mustauto/types';
import { maskCnpj, cleanCnpj, isValidCnpj, maskCep, cleanCep, isValidCep, lookupCep } from '@mustauto/utils';

interface WorkshopDataStepProps {
  onNext: () => void;
  onBack: () => void;
}

const DAY_NAMES = ['Domingo', 'Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado'];

const DEFAULT_HOURS = DAY_NAMES.map((_, i) => ({
  day: i,
  open: '08:00',
  close: '18:00',
  closed: i === 0,
}));

export function WorkshopDataStep({ onNext, onBack }: WorkshopDataStepProps) {
  const { profile, updateProfile, getPresignedUrl, isSaving } = useWorkshopProfile();

  const [name, setName] = useState(profile?.name ?? '');
  const [cnpj, setCnpj] = useState(maskCnpj(profile?.cnpj ?? ''));
  const [cep, setCep] = useState(maskCep(profile?.address?.zip ?? ''));
  const [cnpjError, setCnpjError] = useState<string | null>(null);
  const [cepError, setCepError] = useState<string | null>(null);
  const [ufError, setUfError] = useState<string | null>(null);
  const [street, setStreet] = useState(profile?.address?.street ?? '');
  const [number, setNumber] = useState(profile?.address?.number ?? '');
  const [complement, setComplement] = useState(profile?.address?.complement ?? '');
  const [neighborhood, setNeighborhood] = useState(profile?.address?.neighborhood ?? '');
  const [city, setCity] = useState(profile?.address?.city ?? '');
  const [addrState, setAddrState] = useState(profile?.address?.state ?? '');
  const [specialties, setSpecialties] = useState<string[]>(
    profile?.settings.specialties ?? [],
  );
  const [hours, setHours] = useState(
    profile?.settings.hours?.length === 7 ? profile.settings.hours : DEFAULT_HOURS,
  );
  const [uploading, setUploading] = useState(false);
  const [cepLoading, setCepLoading] = useState(false);

  // Sync from profile when it loads
  useEffect(() => {
    if (profile) {
      if (!name) setName(profile.name);
      if (!cnpj) setCnpj(maskCnpj(profile.cnpj));
      if (profile.address) {
        if (!cep && profile.address.zip) setCep(maskCep(profile.address.zip));
        if (!street && profile.address.street) setStreet(profile.address.street);
        if (!number && profile.address.number) setNumber(profile.address.number);
        if (!complement && profile.address.complement) setComplement(profile.address.complement);
        if (!neighborhood && profile.address.neighborhood) setNeighborhood(profile.address.neighborhood);
        if (!city && profile.address.city) setCity(profile.address.city);
        if (!addrState && profile.address.state) setAddrState(profile.address.state);
      }
      if (specialties.length === 0 && profile.settings.specialties?.length) {
        setSpecialties(profile.settings.specialties);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile?.id]);

  const fetchCep = useCallback(async (rawCep: string) => {
    const clean = cleanCep(rawCep);
    if (clean.length !== 8) return;
    setCepLoading(true);
    try {
      const found = await lookupCep(clean);
      if (found) {
        setStreet(found.street);
        setNeighborhood(found.neighborhood);
        setCity(found.city);
        setAddrState(found.state);
      }
    } finally {
      setCepLoading(false);
    }
  }, []);

  const handleCepChange = useCallback(
    (value: string) => {
      const masked = maskCep(value);
      setCep(masked);
      if (cleanCep(masked).length === 8) {
        fetchCep(masked);
      }
    },
    [fetchCep],
  );

  const toggleSpecialty = useCallback((spec: string) => {
    setSpecialties((prev) =>
      prev.includes(spec) ? prev.filter((s) => s !== spec) : [...prev, spec],
    );
  }, []);

  const applyWeekdayDefaults = useCallback(() => {
    setHours((prev) =>
      prev.map((h) => ({
        ...h,
        open: h.day >= 1 && h.day <= 5 ? '08:00' : h.open,
        close: h.day >= 1 && h.day <= 5 ? '18:00' : h.close,
        closed: h.day === 0 || h.day === 6,
      })),
    );
  }, []);

  const updateHour = useCallback(
    (day: number, field: 'open' | 'close' | 'closed', value: string | boolean) => {
      setHours((prev) =>
        prev.map((h) => (h.day === day ? { ...h, [field]: value } : h)),
      );
    },
    [],
  );

  const handlePhotoUpload = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file || !profile) return;
      const currentPhotos = profile.settings.photos ?? [];
      if (currentPhotos.length >= 3) return;

      setUploading(true);
      try {
        const { uploadUrl, s3Key } = await getPresignedUrl();
        await fetch(uploadUrl, {
          method: 'PUT',
          body: file,
          headers: { 'Content-Type': 'image/jpeg' },
        });
        await updateProfile({ photos: [...currentPhotos.map(toPhotoKey), s3Key] });
      } finally {
        setUploading(false);
        e.target.value = '';
      }
    },
    [profile, getPresignedUrl, updateProfile],
  );

  const handleRemovePhoto = useCallback(
    async (index: number) => {
      if (!profile) return;
      const photos = [...(profile.settings.photos ?? [])];
      photos.splice(index, 1);
      await updateProfile({ photos: photos.map(toPhotoKey) });
    },
    [profile, updateProfile],
  );

  const handleNext = useCallback(async () => {
    // CNPJ é opcional, mas se preenchido tem que passar no mod-11.
    // F-UI Phase 3: convenção do sprint — forms primeira-classe (onboarding,
    // partner reg) validam strict; SuppliersTab fica lenient pra editar
    // suppliers pré-existentes em prod com CNPJ malformado.
    const cnpjCanonical = cleanCnpj(cnpj);
    if (cnpjCanonical && !isValidCnpj(cnpjCanonical)) {
      setCnpjError('CNPJ inválido. Verifique os dígitos.');
      return;
    }
    setCnpjError(null);

    // CEP e UF são os dois campos de formato fechado na API (regex de 8
    // dígitos, enum de UF). Como o endereço vai inteiro, valor pela metade
    // viraria 400 cru; vazio passa, porque vazio é remoção.
    const cepCanonical = cleanCep(cep);
    if (cepCanonical && !isValidCep(cepCanonical)) {
      setCepError('CEP incompleto. Informe os 8 dígitos ou deixe vazio.');
      return;
    }
    setCepError(null);

    const uf = addrState.trim().toUpperCase();
    if (uf && !(UF_VALUES as readonly string[]).includes(uf)) {
      setUfError('UF inválida. Use a sigla de duas letras (ex.: SP).');
      return;
    }
    setUfError(null);

    await updateProfile({
      name,
      cnpj: cnpjCanonical,
      // Endereço vai inteiro, campo vazio incluído: este passo é revisitável
      // (Voltar no wizard, e ele semeia do perfil já salvo), então o spread
      // condicional de antes tornava impossível apagar um campo — a chave
      // sumia do corpo, o PATCH pulava a coluna e o valor antigo voltava.
      addressZip: cepCanonical,
      addressStreet: street.trim(),
      addressNumber: number.trim(),
      addressComplement: complement.trim(),
      addressNeighborhood: neighborhood.trim(),
      addressCity: city.trim(),
      addressState: uf,
      specialties,
      hours,
    });
    onNext();
  }, [name, cnpj, cep, street, number, complement, neighborhood, city, addrState, specialties, hours, updateProfile, onNext]);

  const photos = profile?.settings.photos ?? [];
  // F-UI Bug B.1: ver Settings.tsx pra contexto. Mesma fix.
  const s3Base =
    import.meta.env.VITE_S3_PUBLIC_URL ||
    'https://s3.mustauto.com.br/mustauto-media-public';
  const photoSrc = (p: string): string =>
    p.startsWith('http://') || p.startsWith('https://') ? p : `${s3Base}/${p}`;
  const toPhotoKey = (p: string): string =>
    p.match(/\/(media\/[^?]+)/)?.[1] ?? (p.startsWith('media/') ? p : p);

  return (
    <div className="flex flex-1 flex-col overflow-y-auto px-6 py-4">
      <h2 className="text-xl font-bold">Dados da Oficina</h2>
      <p className="mt-1 text-sm text-muted-foreground">
        Preencha as informações básicas. Você pode alterar tudo depois em Configurações.
      </p>

      <div className="mt-6 space-y-6">
        {/* Nome + CNPJ */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-muted-foreground">
              Nome fantasia *
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              maxLength={255}
              placeholder="Ex: Auto Center Silva"
              className="rounded-md border border-input bg-background px-3 py-2 text-sm"
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-muted-foreground">
              CNPJ (opcional)
            </label>
            <input
              type="text"
              value={cnpj}
              onChange={(e) => {
                setCnpj(maskCnpj(e.target.value));
                if (cnpjError) setCnpjError(null);
              }}
              maxLength={18}
              placeholder="00.000.000/0000-00"
              className={`rounded-md border bg-background px-3 py-2 text-sm ${
                cnpjError ? 'border-destructive' : 'border-input'
              }`}
            />
            {cnpjError && (
              <span className="text-xs text-destructive">{cnpjError}</span>
            )}
          </div>
        </div>

        {/* Endereço estruturado */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-4">
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-muted-foreground">CEP</label>
            <div className="relative">
              <input
                type="text"
                value={cep}
                onChange={(e) => {
                  handleCepChange(e.target.value);
                  if (cepError) setCepError(null);
                }}
                maxLength={9}
                placeholder="00000-000"
                className={`w-full rounded-md border bg-background px-3 py-2 text-sm ${
                  cepError ? 'border-destructive' : 'border-input'
                }`}
              />
              {cepLoading && (
                <div className="absolute right-2 top-2.5 h-4 w-4 animate-spin rounded-full border-2 border-primary border-t-transparent" />
              )}
            </div>
            {cepError && <span className="text-xs text-destructive">{cepError}</span>}
          </div>
          <div className="flex flex-col gap-1 sm:col-span-2">
            <label className="text-xs font-medium text-muted-foreground">Rua</label>
            <input
              type="text"
              value={street}
              onChange={(e) => setStreet(e.target.value)}
              maxLength={200}
              placeholder="Rua / Avenida"
              className="rounded-md border border-input bg-background px-3 py-2 text-sm"
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-muted-foreground">Número</label>
            <input
              type="text"
              value={number}
              onChange={(e) => setNumber(e.target.value)}
              maxLength={20}
              placeholder="123"
              className="rounded-md border border-input bg-background px-3 py-2 text-sm"
            />
          </div>
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-4">
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-muted-foreground">Complemento</label>
            <input
              type="text"
              value={complement}
              onChange={(e) => setComplement(e.target.value)}
              maxLength={100}
              placeholder="Sala 2"
              className="rounded-md border border-input bg-background px-3 py-2 text-sm"
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-muted-foreground">Bairro</label>
            <input
              type="text"
              value={neighborhood}
              onChange={(e) => setNeighborhood(e.target.value)}
              maxLength={100}
              placeholder="Centro"
              className="rounded-md border border-input bg-background px-3 py-2 text-sm"
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-muted-foreground">Cidade</label>
            <input
              type="text"
              value={city}
              onChange={(e) => setCity(e.target.value)}
              maxLength={100}
              placeholder="São Paulo"
              className="rounded-md border border-input bg-background px-3 py-2 text-sm"
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-muted-foreground">Estado</label>
            <input
              type="text"
              value={addrState}
              onChange={(e) => {
                setAddrState(e.target.value.toUpperCase());
                if (ufError) setUfError(null);
              }}
              maxLength={2}
              placeholder="SP"
              className={`rounded-md border bg-background px-3 py-2 text-sm ${
                ufError ? 'border-destructive' : 'border-input'
              }`}
            />
            {ufError && <span className="text-xs text-destructive">{ufError}</span>}
          </div>
        </div>

        {/* Especialidades */}
        <div className="flex flex-col gap-2">
          <label className="text-xs font-medium text-muted-foreground">
            Especialidades
          </label>
          <SpecialtyPicker value={specialties} onToggle={toggleSpecialty} />
        </div>

        {/* Horarios */}
        <div className="flex flex-col gap-2">
          <div className="flex items-center justify-between">
            <label className="text-xs font-medium text-muted-foreground">
              Horários de funcionamento
            </label>
            <button
              type="button"
              onClick={applyWeekdayDefaults}
              className="text-xs text-blue-600 dark:text-blue-400 hover:underline"
            >
              Dias úteis 08h-18h
            </button>
          </div>
          <div className="space-y-2">
            {hours.map((h) => (
              <div key={h.day} className="flex items-center gap-3">
                <span className="w-20 text-sm font-medium">{DAY_NAMES[h.day]}</span>
                <label className="flex items-center gap-1.5 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={h.closed}
                    onChange={(e) => updateHour(h.day, 'closed', e.target.checked)}
                    className="rounded border-input"
                  />
                  <span className="text-xs text-muted-foreground">Fechado</span>
                </label>
                {!h.closed && (
                  <>
                    <input
                      type="time"
                      value={h.open}
                      onChange={(e) => updateHour(h.day, 'open', e.target.value)}
                      className="rounded-md border border-input bg-background px-2 py-1 text-sm"
                    />
                    <span className="text-xs text-muted-foreground">até</span>
                    <input
                      type="time"
                      value={h.close}
                      onChange={(e) => updateHour(h.day, 'close', e.target.value)}
                      className="rounded-md border border-input bg-background px-2 py-1 text-sm"
                    />
                  </>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Photos */}
        <div className="flex flex-col gap-2">
          <label className="text-xs font-medium text-muted-foreground">
            Fotos da oficina (max 3)
          </label>
          <div className="flex flex-wrap gap-3">
            {photos.map((key, i) => (
              <div
                key={key}
                className="relative h-20 w-20 rounded-lg border border-border overflow-hidden group"
              >
                <img
                  src={photoSrc(key)}
                  alt={`Foto ${i + 1}`}
                  className="h-full w-full object-cover"
                />
                <button
                  onClick={() => handleRemovePhoto(i)}
                  aria-label="Remover foto"
                  className="absolute top-1 right-1 rounded-full bg-destructive p-0.5 text-destructive-foreground opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <X size={12} />
                </button>
              </div>
            ))}
            {photos.length < 3 && (
              <label className="flex h-20 w-20 cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed border-border text-muted-foreground hover:border-primary/50 transition-colors">
                {uploading ? (
                  <div className="h-5 w-5 animate-spin rounded-full border-2 border-primary border-t-transparent" />
                ) : (
                  <>
                    <Upload size={18} />
                    <span className="mt-1 text-[10px]">Upload</span>
                  </>
                )}
                <input
                  type="file"
                  accept="image/jpeg,image/png"
                  onChange={handlePhotoUpload}
                  className="hidden"
                  disabled={uploading}
                />
              </label>
            )}
          </div>
        </div>
      </div>

      {/* Nav buttons */}
      <div className="mt-8 flex items-center justify-between">
        <button
          onClick={onBack}
          className="rounded-lg border border-border px-4 py-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
        >
          Voltar
        </button>
        <button
          onClick={handleNext}
          disabled={!name.trim() || isSaving}
          className="rounded-lg bg-primary px-6 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors disabled:opacity-50"
        >
          {isSaving ? 'Salvando...' : 'Continuar'}
        </button>
      </div>
    </div>
  );
}
